import { GenericAbortSignal } from 'axios';

import { AttrValue, AttrValueOptions } from 'models/flexible/AttrValue';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminAttrValueCreateParams,
  AdminAttrValueIndexFields,
  AdminAttrValueIndexInclude,
  AdminAttrValueShowFields,
  AdminAttrValueShowInclude,
  AdminAttrValueUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminAttrValuesQueryKeys = {
  index: (params?: Record<string, any>) => ['adminAttrValues', params] as const,
  show: (id: string) => ['adminAttrValues', id] as const,
};

const URL = 'admin/attr_values';

export type AdminAttrValueIndexParams = BaseIndexQueryParams & {
  client?: string;
  attrDisplayName?: string;
  olioAttr?: string;
  onlyInactive?: string;
};

export async function adminIndexAttrValues<I extends AdminAttrValueIndexInclude = never>(
  params: AdminAttrValueIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<AttrValue<AdminAttrValueIndexFields<I>>>> {
  return http
    .get<
      Paginated<AttrValueOptions>
    >(URL, { params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new AttrValue(x)),
    })) as unknown as Promise<Paginated<AttrValue<AdminAttrValueIndexFields<I>>>>;
}

export async function adminShowAttrValue<I extends AdminAttrValueShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<AttrValue<AdminAttrValueShowFields<I>>> {
  return http
    .get<AttrValueOptions>(`${URL}/${id}`, { params: { include: includes?.join(',') }, signal })
    .then((res) => new AttrValue(res.data)) as unknown as Promise<AttrValue<AdminAttrValueShowFields<I>>>;
}

export async function createAttrValue(params: AdminAttrValueCreateParams) {
  return http.post<AttrValueOptions>(URL, params).then((res) => new AttrValue(res.data));
}

export async function updateAttrValue(params: AdminAttrValueUpdateParams) {
  const { id, ...body } = params;
  return http.patch<AttrValueOptions>(`${URL}/${id}`, body).then((res) => new AttrValue(res.data));
}
